import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, Copy, FileText, RefreshCw, Search, X } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";

const normalizeEpisodeTitle = (episode, index = 0) => {
  const order = episode?.sortOrder || episode?.episodeNumber || index + 1;
  const rawTitle = episode?.title || episode?.name || "未命名";
  const prefix = `第${order}集`;
  const cleanTitle = String(rawTitle)
    .replace(new RegExp(`^第\\s*${order}\\s*集\\s*[：:·\\-—_\\s]*`), "")
    .replace(/^第\s*\d+\s*集\s*[：:·\-—_\s]*/, "")
    .trim();
  return cleanTitle ? `${prefix} ${cleanTitle}` : prefix;
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const scriptOf = (episode) => firstText(episode?.scriptContent, episode?.content, episode?.text);

const shortText = (value, max = 82) => {
  const text = value || "";
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const buildTextParts = (text, keyword) => {
  const source = text || "";
  const query = (keyword || "").trim();
  if (!query) return [{ text: source, hit: false }];
  const lowerSource = source.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const result = [];
  let cursor = 0;
  let nextIndex = lowerSource.indexOf(lowerQuery, cursor);
  while (nextIndex !== -1) {
    if (nextIndex > cursor) result.push({ text: source.slice(cursor, nextIndex), hit: false });
    result.push({ text: source.slice(nextIndex, nextIndex + query.length), hit: true });
    cursor = nextIndex + query.length;
    nextIndex = lowerSource.indexOf(lowerQuery, cursor);
  }
  if (cursor < source.length) result.push({ text: source.slice(cursor), hit: false });
  return result;
};

const countMatches = (text, keyword) => {
  const source = (text || "").toLowerCase();
  const query = (keyword || "").trim().toLowerCase();
  if (!source || !query) return 0;
  let count = 0;
  let cursor = source.indexOf(query);
  while (cursor !== -1) {
    count += 1;
    cursor = source.indexOf(query, cursor + query.length);
  }
  return count;
};

const GlobalScriptViewer = ({ isOpen, onClose, projectId, currentEpisodeId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const searchInputRef = useRef(null);
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(currentEpisodeId ? String(currentEpisodeId) : "");
  const [episodeFilter, setEpisodeFilter] = useState("");
  const [scriptSearch, setScriptSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const visibility = useDelayedUnmount(Boolean(isOpen), 160);

  const loadEpisodes = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
      const list = Array.isArray(data) ? data : [];
      setEpisodes(list);
      setSelectedEpisodeId((current) => {
        if (current && list.some((episode) => String(episode.id) === String(current))) return current;
        if (currentEpisodeId && list.some((episode) => String(episode.id) === String(currentEpisodeId))) return String(currentEpisodeId);
        const firstWithScript = list.find((episode) => scriptOf(episode));
        return firstWithScript?.id ? String(firstWithScript.id) : (list[0]?.id ? String(list[0].id) : "");
      });
    } catch (err) {
      setError(err.message || "剧本加载失败");
    } finally {
      setLoading(false);
    }
  }, [currentEpisodeId, projectId]);

  useEffect(() => {
    if (!isOpen) return;
    loadEpisodes();
  }, [isOpen, loadEpisodes]);

  useEffect(() => {
    if (isOpen && currentEpisodeId) {
      setSelectedEpisodeId(String(currentEpisodeId));
    }
  }, [currentEpisodeId, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const selectedEpisode = useMemo(
    () => episodes.find((episode) => String(episode.id) === String(selectedEpisodeId)) || null,
    [episodes, selectedEpisodeId]
  );

  const selectedIndex = useMemo(
    () => episodes.findIndex((episode) => String(episode.id) === String(selectedEpisodeId)),
    [episodes, selectedEpisodeId]
  );

  const filteredEpisodes = useMemo(() => {
    const keyword = episodeFilter.trim().toLowerCase();
    if (!keyword) return episodes;
    return episodes.filter((episode, index) => {
      const title = normalizeEpisodeTitle(episode, index).toLowerCase();
      const content = scriptOf(episode).toLowerCase();
      return title.includes(keyword) || content.includes(keyword);
    });
  }, [episodeFilter, episodes]);

  const selectedScript = scriptOf(selectedEpisode);
  const highlightedParts = useMemo(
    () => buildTextParts(selectedScript, scriptSearch),
    [scriptSearch, selectedScript]
  );
  const matchCount = useMemo(
    () => countMatches(selectedScript, scriptSearch),
    [scriptSearch, selectedScript]
  );

  const copyScript = async () => {
    if (!selectedScript) return;
    try {
      await navigator.clipboard.writeText(selectedScript);
      toast.success("已复制当前集剧本");
    } catch (err) {
      toast.error(err.message || "复制失败");
    }
  };

  if (!visibility.mounted) return null;

  return (
    <div
      className={`fixed inset-0 z-[70] flex items-center justify-center px-3 py-4 md:px-6 ${visibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}
      style={{ backgroundColor: "var(--overlay-light)", backdropFilter: "blur(10px)" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        className={`w-full h-full max-h-[860px] max-w-[1180px] md:h-[82vh] rounded-xl border shadow-2xl overflow-hidden flex flex-col ${visibility.exiting ? "ui-modal-exit" : "ui-modal-enter"}`}
        style={{
          backgroundColor: "var(--color-bg-secondary)",
          borderColor: "var(--color-border-primary)",
          boxShadow: "var(--shadow-modal)",
        }}
      >
        <header className="h-14 shrink-0 border-b px-4 flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)" }}>
          <div className="min-w-0 flex items-center gap-2">
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
            >
              <BookOpen size={18} />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-primary)" }}>剧本快览</div>
              <div className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>
                {episodes.length ? `${episodes.length} 集` : "未加载"} · 当前页不跳转
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={loadEpisodes}
              disabled={loading || !projectId}
              className="ui-pressable h-9 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
              style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              刷新
            </button>
            <button
              type="button"
              onClick={onClose}
              className="ui-pressable w-9 h-9 rounded-md border flex items-center justify-center cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
              style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
              aria-label="关闭剧本快览"
              title="关闭"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="min-h-0 border-b md:border-b-0 md:border-r flex flex-col" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)" }}>
            <div className="p-3 shrink-0 space-y-2">
              <div className="h-9 rounded-md border px-3 flex items-center gap-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                <Search size={14} style={{ color: "var(--color-text-muted)" }} />
                <input
                  value={episodeFilter}
                  onChange={(event) => setEpisodeFilter(event.target.value)}
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                  placeholder="搜索集数或剧本文字"
                />
                {episodeFilter && (
                  <button
                    type="button"
                    onClick={() => setEpisodeFilter("")}
                    className="w-6 h-6 rounded flex items-center justify-center cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors"
                    style={{ color: "var(--color-text-muted)" }}
                    aria-label="清空集数搜索"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-3 pb-3 space-y-1.5">
              {loading && !episodes.length ? (
                <div className="space-y-2 pt-1">
                  {[0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-14" />)}
                </div>
              ) : filteredEpisodes.length ? filteredEpisodes.map((episode, index) => {
                const originalIndex = episodes.findIndex((item) => String(item.id) === String(episode.id));
                const title = normalizeEpisodeTitle(episode, originalIndex >= 0 ? originalIndex : index);
                const active = String(episode.id) === String(selectedEpisodeId);
                const content = scriptOf(episode);
                return (
                  <button
                    key={episode.id}
                    type="button"
                    onClick={() => setSelectedEpisodeId(String(episode.id))}
                    className="w-full rounded-md border p-3 text-left cursor-pointer transition-colors"
                    style={{
                      borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                      backgroundColor: active ? "var(--color-accent-light)" : "var(--color-bg-secondary)",
                    }}
                  >
                    <div className="text-sm font-semibold truncate" style={{ color: active ? theme.colors.accentPrimary : "var(--color-text-primary)" }}>
                      {title}
                    </div>
                    <div className="mt-1 text-xs leading-5 line-clamp-2" style={{ color: "var(--color-text-muted)" }}>
                      {content ? shortText(content) : "暂无剧本内容"}
                    </div>
                  </button>
                );
              }) : (
                <div className="rounded-md border p-4 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
                  没有匹配的集数。
                </div>
              )}
            </div>
          </aside>

          <main className="min-h-0 flex flex-col">
            <div className="shrink-0 border-b p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-bold truncate" style={{ color: "var(--color-text-primary)" }}>
                    {selectedEpisode ? normalizeEpisodeTitle(selectedEpisode, selectedIndex >= 0 ? selectedIndex : 0) : "请选择一集"}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                    {(selectedScript || "").length.toLocaleString()} 字{scriptSearch.trim() ? ` · 命中 ${matchCount} 处` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={copyScript}
                    disabled={!selectedScript}
                    className="h-9 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50 cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors"
                    style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
                  >
                    <Copy size={14} />
                    复制
                  </button>
                </div>
              </div>

              <div className="h-10 rounded-md border px-3 flex items-center gap-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                <Search size={15} style={{ color: "var(--color-text-muted)" }} />
                <input
                  ref={searchInputRef}
                  value={scriptSearch}
                  onChange={(event) => setScriptSearch(event.target.value)}
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                  placeholder="在当前集剧本里搜索"
                />
                {scriptSearch && (
                  <button
                    type="button"
                    onClick={() => setScriptSearch("")}
                    className="w-7 h-7 rounded flex items-center justify-center cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                    style={{ color: "var(--color-text-muted)" }}
                    aria-label="清空剧本搜索"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
              {error ? (
                <div className="m-5 rounded-md border p-4 text-sm" style={{ borderColor: "var(--color-error)", color: "var(--color-error)", backgroundColor: "rgba(220,38,38,0.08)" }}>
                  {error}
                </div>
              ) : selectedScript ? (
                <pre className="px-5 md:px-8 py-5 text-base leading-8 whitespace-pre-wrap break-words font-sans" style={{ color: "var(--color-text-secondary)" }}>
                  {highlightedParts.map((part, index) => part.hit ? (
                    <mark
                      key={index}
                      className="rounded px-0.5"
                      style={{ backgroundColor: `${theme.colors.accentPrimary}32`, color: "var(--color-text-primary)" }}
                    >
                      {part.text}
                    </mark>
                  ) : (
                    <React.Fragment key={index}>{part.text}</React.Fragment>
                  ))}
                </pre>
              ) : (
                <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
                  <FileText size={34} style={{ color: "var(--color-text-muted)", opacity: 0.35 }} />
                  <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>暂无剧本内容</div>
                  <div className="text-xs leading-6 max-w-[360px]" style={{ color: "var(--color-text-muted)" }}>
                    当前项目还没有可查看的单集剧本。
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </section>
    </div>
  );
};

export default GlobalScriptViewer;
